/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { AppRouter } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { KeycloakService } from '@gh/core-auth-keycloak';
import { TranslationService } from '@gh/core-mls';
import { DialogService } from '@gh/core-ui';
import { getUrlSegment, openInThisWindow, replaceUrlSegment } from '@gh/core-util';
import { filter$ } from '@gh/rx/operators';
import { OfficeInfo, SupplierUserContext, SupplierUserData } from '@gh/supplier-shared';
import { Subscription } from 'rxjs';
import { supplierOfficeInfoDialogRoute } from '../supplier-office-info-dialog/supplier-office-info-dialog.component';

const ALL_OFFICES: OfficeInfo = <any>{};
const EMPTY_OFFICE: OfficeInfo = <any>{};
const ROUTE_CALENDAR: string = 'calendar';
const ROUTE_ORDERS: string = 'purchase-orders';

@Component({
  selector: 'gh-supplier-app',
  styleUrls: ['supplier-app.component.scss'],
  templateUrl: 'supplier-app.component.html',
})
export class SupplierAppComponent implements OnInit, OnDestroy {
  allOffices = ALL_OFFICES;
  offices: OfficeInfo[];
  hasOffices: boolean;
  isCalendarRoute: boolean = false;
  isOrdersRoute: boolean = false;
  currentSelectedOffice: OfficeInfo = EMPTY_OFFICE;
  isMediaLtMd: boolean = false;
  infiniteScrollViewport?: string;
  private mediaSubscription: Subscription;

  get currentOffice(): OfficeInfo {
    return this.userContext.currentOffice || EMPTY_OFFICE;
  }

  get receiveEmails(): boolean {
    return (<SupplierUserData>this.userContext.data).receiveEmails;
  }

  get officeId(): ID {
    return this.userContext.currentOffice.id;
  }

  get groupedOfficeId(): ID {
    if (this.isCalendarRoute || this.isOrdersRoute) {
      return this.currentSelectedOffice === ALL_OFFICES ? 'all' : this.currentSelectedOffice.id;
    } else {
      return 'all';
    }
  }

  get hasSeveralOffices(): boolean {
    return this.userContext.offices.length > 1;
  }

  get isAllOfficesOptionVisible(): boolean {
    return this.hasSeveralOffices && (this.isCalendarRoute || this.isOrdersRoute);
  }

  get isOfficeSelectorVisible(): boolean {
    return !this.isCalendarRoute || (this.isCalendarRoute && this.isMediaLtMd);
  }

  constructor(private observer: MediaObserver,
              public userContext: SupplierUserContext,
              private dialogs: DialogService,
              private appRouter: AppRouter,
              private router: Router,
              private translations: TranslationService,
              @Optional() private keycloak?: KeycloakService) {
  }

  ngOnInit(): void {
    // user context may be not initialized in case of error on login
    if (!this.userContext.isInitialized()) {
      return;
    }

    const { offices } = this.userContext;
    this.offices = offices.length > 0 ? offices : [EMPTY_OFFICE];
    this.hasOffices = offices.length > 0;

    this.mediaSubscription = this.observer.media$
      .subscribe(() => {
        this.infiniteScrollViewport = this.observer.isActive('lt-md') ? 'root' : void 0;
        this.isMediaLtMd = this.observer.isActive('lt-md');
      });

    this.router.events
      .pipe(filter$((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isCalendarRoute = Boolean(event.url.match(ROUTE_CALENDAR));
        this.isOrdersRoute = Boolean(event.url.match(ROUTE_ORDERS));

        const officeId = getUrlSegment(this.appRouter.url, 1);
        this.currentSelectedOffice = officeId === 'all'
          ? ALL_OFFICES
          : (this.userContext.getOffice(officeId) || EMPTY_OFFICE);
      });
  }

  ngOnDestroy(): void {
    if (this.mediaSubscription) {
      this.mediaSubscription.unsubscribe();
    }
  }

  onCurrentOfficeChange(office: OfficeInfo): void {
    this.appRouter.navigateByUrl(replaceUrlSegment(
      this.appRouter.url,
      1,
      office === ALL_OFFICES ? 'all' : office.id));
  }

  onOpenOfficeInfo(office: OfficeInfo): void {
    this.dialogs.openWithInput(supplierOfficeInfoDialogRoute, {
      office: this.currentOffice,
    });
  }

  onChangePassword(): void {
    if (this.keycloak) {
      openInThisWindow(this.keycloak.getChangePasswordUrl());
    }
  }

  onReceiveEmailsChange(receiveEmails: boolean): void {
    this.userContext.updateReceiveEmails(receiveEmails);
  }

  onLogout(): void {
    this.userContext.logout();
  }
}
